import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as actions from '../../store/actions/thunks';
import * as AuthService from '../../services/AuthService';
import { readStoredUser, writeStoredUser } from '../../utils/auth/userStorage';
import {COLORS, FONT_SIZE, FONT_WEIGHT} from '../../components/ui/theme';
import { ModalShell } from '../../components/ui/ModalShell';
import {
  ModalHeader,
  ModalHeaderTitle,
  ModalCloseBtn,
  ModalBody,
  ModalFooter,
  ModalIconBadge as ModalIconBadgeBase,
  ButtonPrimary,
  ButtonSecondary,
  FormRow,
  FormLabel,
  FormInput,
} from '../../components/ui/ModalDA';

/* Cast TS pour transient props ($bg/$border/$size/$mb) — styled-components sans typing strict.
   Utilise `as unknown as ComponentType` pour bypass le typing JSX de styled-components qui
   ne reconnaît pas les transient props ($-prefixed) sans déclaration TS dédiée. */
const ModalIconBadge = ModalIconBadgeBase as unknown as React.ComponentType<{
  $bg?: string;
  $border?: boolean;
  $size?: string;
  $mb?: string;
  children?: React.ReactNode;
}>;

/**
 * CodeVerification — Modal de saisie du code de vérification email (after registration/login).
 *
 * Migration P3 (2026-06-06) : MUI Dialog + Button + Typography + TextField + FormHelperText
 * + CheckCircleOutlinedIcon → ModalShell + primitives DA + FormRow/Label/Input.
 *
 * 2 états :
 *   1. Saisie du code : input + boutons "Resend" / "Verify"
 *   2. Email vérifié : badge check vert + bouton "Continue"
 *
 * @see tasks/active/TRACK-2026-06-06-da-middleware-p3-batch1.md
 */

interface IModalCodeVerification {
  open: boolean;
  onClose: () => void;
  formData: any;
  isRegister: boolean;
}

const Title = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.ink};
  text-align: center;
`;

const SubTitle = styled.p`
  margin: 0 0 0.5rem;
  font-size: ${FONT_SIZE.base};
  color: ${COLORS.ink};
  text-align: center;
`;

const HelperText = styled.p`
  margin: 0 0 1rem;
  font-size: ${FONT_SIZE.sm};
  color: ${COLORS.muted};
  text-align: center;
  line-height: 1.45;
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 0.375rem;
  font-size: 0.8125rem;
  color: ${COLORS.danger};
  text-align: center;
`;

const ResendConfirm = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.8125rem;
  color: ${COLORS.cta};
  text-align: center;
  line-height: 1.45;
`;

/* SVG check vert (remplace MUI CheckCircleOutlinedIcon) */
const CheckSVG = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke={COLORS.cta} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

function CodeVerification({ open, onClose, formData, isRegister }: IModalCodeVerification) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [isNotMatched, setIsNotMatched] = useState(false);
  const [isEmailVerified, setEmailVerified] = useState(false);
  const [isResent, setIsResent] = useState(false);

  useEffect(() => { setIsResent(false); }, [open]);

  const handleVerifiyEmail = async () => {
    const data = {
      metamask: formData.get('metamask'),
      code: input.toString().trim(),
      isRegister,
    };
    try {
      const res = await AuthService.verify(data);
      setIsNotMatched(false);
      setEmailVerified(true);
      writeStoredUser(res);
      localStorage.setItem('_first', 'true');
      dispatch(actions.setUser(res));
    } catch (err) {
      setIsNotMatched(true);
    }
  };

  const confirmRegister = async () => {
    onClose();
    setEmailVerified(false);
    window.location.href = '/';
  };

  const handleResend = async () => {
    const u = readStoredUser() || {};
    await AuthService.resend({ metamask: u.metamask });
    setIsResent(true);
  };

  return (
    <ModalShell isOpen={open} onClose={onClose} $width="32rem" role="dialog" aria-label="Email verification">
      <ModalHeader>
        <ModalHeaderTitle>{t('WelcomeToEarniverse')}</ModalHeaderTitle>
        <ModalCloseBtn onClick={onClose} aria-label="Close">✕</ModalCloseBtn>
      </ModalHeader>
      <ModalBody>
        {isEmailVerified ? (
          <>
            <ModalIconBadge $bg={COLORS.accentSoft} $border $size="5rem" $mb="1.25rem">
              <CheckSVG />
            </ModalIconBadge>
            <Title>{t('EmailVerifiedTitle')}</Title>
          </>
        ) : (
          <>
            <SubTitle>{t('VerifyEmail')}</SubTitle>
            <HelperText>
              {t('EmailVerificationHelp')}
            </HelperText>
            <FormRow>
              <FormLabel htmlFor="codeVerificationInput">{t('Code')}</FormLabel>
              <FormInput
                id="codeVerificationInput"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('EnterCodePlaceholder')}
              />
              {isNotMatched && <ErrorText>{t('CodeVerification')}</ErrorText>}
            </FormRow>
            {isResent && (
              <ResendConfirm>
                {t('EmailResendConfirm')}
              </ResendConfirm>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {isEmailVerified ? (
          <ButtonPrimary onClick={confirmRegister}>{t('Continue')}</ButtonPrimary>
        ) : (
          <>
            <ButtonSecondary onClick={handleResend}>{t('Resend')}</ButtonSecondary>
            <ButtonPrimary onClick={handleVerifiyEmail}>{t('Verify')}</ButtonPrimary>
          </>
        )}
      </ModalFooter>
    </ModalShell>
  );
}

export default CodeVerification;
